import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import {
  CREW_DOCUMENT_CATEGORIES,
  CrewDocumentInput,
  CrewDocumentRecord,
  getDocumentName,
} from "../types/crew-documents";

const ACCEPTED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "doc",
  "docx",
] as const;
const ACCEPT_ATTRIBUTE = ".pdf,.png,.jpg,.jpeg,.doc,.docx";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export interface CrewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CrewDocumentInput) => Promise<boolean>;
  saving: boolean;
  /** Present when editing an existing document. */
  record?: CrewDocumentRecord | null;
  /** Pre-selected category when uploading into a specific section. */
  defaultTypeName?: string;
}

export const CrewDocumentDialog: React.FC<CrewDocumentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  saving,
  record,
  defaultTypeName,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const isEdit = Boolean(record);
  const [typeName, setTypeName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [typeError, setTypeError] = useState<string>("");

  const categoryOptions = useMemo(
    () => CREW_DOCUMENT_CATEGORIES.map((category) => category.type_name),
    [],
  );

  useEffect(() => {
    if (!open) return;
    setTypeName(record?.type_name ?? defaultTypeName ?? "");
    setFile(null);
    setFileError("");
    setTypeError("");
  }, [open, record, defaultTypeName]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    // Allow re-picking the same file after a failed attempt.
    event.target.value = "";

    if (!selected) return;

    const extension = selected.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ACCEPTED_EXTENSIONS.includes(extension as (typeof ACCEPTED_EXTENSIONS)[number])) {
      setFileError("Unsupported file type. Upload a PDF, image, or Word document.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFileError("File size too large. Maximum size is 10MB.");
      return;
    }

    setFileError("");
    setFile(selected);
  };

  const handleSubmit = async () => {
    const trimmedType = typeName.trim();

    if (!trimmedType) {
      setTypeError("Select or enter a document category.");
      return;
    }
    if (!isEdit && !file) {
      setFileError("Choose a file to upload.");
      return;
    }

    const succeeded = await onSubmit({ typeName: trimmedType, file });
    if (succeeded) onClose();
  };

  const currentFileName = record ? getDocumentName(record.document) : "";

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="crew-document-dialog-title"
    >
      <DialogTitle id="crew-document-dialog-title">
        {isEdit ? "Update Document" : "Upload Document"}
      </DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          freeSolo
          options={categoryOptions}
          value={typeName}
          onChange={(_event, newValue) => {
            setTypeName(newValue ?? "");
            setTypeError("");
          }}
          onInputChange={(_event, newInput) => {
            setTypeName(newInput);
            setTypeError("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Document Category"
              placeholder="e.g. ID Document"
              error={Boolean(typeError)}
              helperText={
                typeError ||
                "Pick a standard category, or type your own for anything else."
              }
            />
          )}
        />

        <Box mt={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            File
          </Typography>

          {isEdit && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              Current file: <strong>{currentFileName}</strong>. Leave the file
              empty to only change the category.
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              p: 2,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: fileError ? "error.main" : colors.primary[800],
            }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={saving}
              sx={{ textTransform: "none", borderRadius: 1.5 }}
            >
              {file ? "Change File" : isEdit ? "Replace File" : "Choose File"}
              <input
                hidden
                type="file"
                accept={ACCEPT_ATTRIBUTE}
                onChange={handleFileChange}
              />
            </Button>

            {file ? (
              <Chip
                label={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
                onDelete={saving ? undefined : () => setFile(null)}
                sx={{ borderRadius: 1.5, maxWidth: "100%" }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                PDF, JPG, PNG, DOC or DOCX — up to 10MB.
              </Typography>
            )}
          </Box>

          {fileError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
              {fileError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
          sx={{
            backgroundColor: reduxTheme.primary_colors[400],
            borderRadius: 1.5,
            textTransform: "none",
          }}
        >
          {isEdit ? "Save Changes" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrewDocumentDialog;
